import { type SiteId } from '@solinkcorp/api-types';
import {
  canAccessAlarmStatusOnly,
  userHasFeature,
  useUserSession,
  type UserSession,
} from '@solinkcorp/authentication';
import { SDM_EVENTS } from '@solinkcorp/service-display-monitor';
import { Button, Flex, Grid, KeyActions } from '@solinkcorp/solink-components';
import { Button as SolinkButton } from '@solinkcorp/solink-ui';
import { AiChatStoreProvider } from '@solinkcorp/solinkii/aiChatStore';
import { useQueryParams, type QueryGetter } from '@solinkcorp/utils/hooks';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import EventActions from 'actions/EventActions';
import { setRange } from 'actions/filterActions';
import { toggleShowKeyboardShortcuts } from 'actions/uiStateActions';
import classNames from 'classnames';
import { KeyboardShortcutsDialogGated } from 'components/NavigationFrame/KeyboardShortcutsDialog';
import NotificationInbox from 'components/Notifications/NotificationInbox';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import SmartSearch from 'components/SmartSearch/Aspen/SmartSearch';
import ThemeVariables from 'env/themeVariables';
import { type Immutable } from 'immer';
import { ExternalLink, LayoutDashboard } from 'lucide-react';
import { SELECT_LOCATIONS_CONTEXT } from 'models/AnalyticsConstants';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import { PureComponent, useMemo } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  type NavigateFunction,
  type Location as RouterLocation,
} from 'react-router-dom';
import { type FilterState } from 'reducers/filterReducer';
import { useControllerState } from 'solox';
import { isKiosk } from 'src/core/utils/kioskUtils';
import useDevicesFromSites from 'src/hooks/devices/useDevicesFromSites';
import { useDevicesAreLocal } from 'src/hooks/useDeviceIsLocal';
import useLocale from 'src/hooks/useLocale';
import { useDispatch, useSelector } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { hasAccessToVideoSearch } from 'utils/permissionUtil';
import { type VideoDiscoverySite } from 'utils/types';
import {
  ALARMS_URL,
  CLIPS_URL,
  DASHBOARD_URL,
  EVENTS_URL,
  getTabFromPathname,
  SETTINGS_CLOUD_RECORDINGS_URL,
  SIDEKICK_URL,
  VERIFEYE4_URL,
  VIDEO_URL_PREFIX,
} from 'utils/urls';
import {
  RANGE_DAY,
  SD_RESTRICTION_THRESHOLD,
  TAB_CLIPS,
  TAB_EVENTS,
  TAB_VIDEO,
} from 'vd-constants';
import GlobalDatePicker from '../../../GlobalDatePicker/GlobalDatePicker';
import LocationSelector from '../../../LocationSelector/LocationSelector';
import {
  applyLocationFilter,
  logout as navigateToLogout,
  goToSettings as navigateToSettings,
  switchUsers as navigateToSwitchUsers,
  goToCallhomeUser as openCallhomeUser,
  goToHelpCenter as openHelpCenter,
  goToReferral as openReferral,
} from '../../navChromeActions';
import {
  buildRangeOptions,
  chevronsStepByRange,
  DATE_PICKER_ROUTES,
  getDateBounds,
  RANGE_PICKER_ROUTES,
} from '../../navigation/dateFilterPolicy';
import UserDropDownMenu from '../../UserDropDownMenu/UserDropDownMenu';
import { buildUserMenuItems } from '../../UserDropDownMenu/userMenuItems';
import AIChat from '../AIChat';
import RangeSelector from '../RangeSelector';
import './DesktopHeader.sass';
import Sidekick from './Sidekick';

const LOCATION_PICKER_ROUTES = [
  ALARMS_URL,
  CLIPS_URL,
  DASHBOARD_URL,
  EVENTS_URL,
  SETTINGS_CLOUD_RECORDINGS_URL,
  VERIFEYE4_URL,
  VIDEO_URL_PREFIX,
  SIDEKICK_URL,
];

const VIDEO_SEARCH_ROUTES = [VIDEO_URL_PREFIX];

const SIDEKICK_ROUTES = [EVENTS_URL, VIDEO_URL_PREFIX];

const COLUMN_GAP = '12px';

interface RouterProps {
  location: RouterLocation;
  navigate: NavigateFunction;
}

interface PlayingCamerasController {
  clearPlayingCameras: () => void;
  state: any;
}

interface DesktopHeaderProps {
  notificationCount?: number;
  onShowAllNotifications?: () => void;
  windowWidth?: number;
  locale: any;
  filter: Immutable<FilterState>;
  numberOfCamerasPlaying: number;
  showKeyboardShortcuts: boolean;
  dispatch: (action: any) => void;
  router: RouterProps;
  user: UserSession;
  playingCamerasController: PlayingCamerasController;
  query: QueryGetter;
  locationsById?: { [locationId: SiteId]: VideoDiscoverySite } | undefined;
  isImpersonating: boolean;
  isLiveToggleOn: boolean;
  handleLiveToggle: (value: boolean) => void;
  locations?: VideoDiscoverySite[];
  timezoneId: string;
  /**
   * True when the sidebar renders the location filter, date filter, range
   * selector, notifications, and profile menu, so the header must not
   * duplicate them.
   */
  hideRelocatedControls?: boolean;
}

class DesktopHeader extends PureComponent<DesktopHeaderProps> {
  private shortcuts: Array<{
    shortcut: string;
    action: () => void;
    isFormSafe: boolean;
    runWhenHelpShowing: boolean;
    info: {
      title: string;
      group: string;
    };
  }>;

  constructor(props: DesktopHeaderProps) {
    super(props);

    const { locale } = props;

    this.shortcuts = [
      {
        shortcut: 'mod+/',
        action: this.toggleShortcutsModal,
        isFormSafe: true,
        runWhenHelpShowing: true,
        info: {
          title: locale.keyboard.viewKeyboardShortcuts,
          group: locale.general.general,
        },
      },
    ];
  }

  getUserMenuItems = () => {
    const { locale, isImpersonating, user } = this.props;
    return buildUserMenuItems({
      locale,
      isImpersonating,
      userEmail: user?.profile?.email || '',
      onGoToSettings: this.goToSettings,
      onToggleKeyboardShortcuts: this.toggleShortcutsModal,
      onGoToHelpCenter: this.goToHelpCenter,
      onGoToReferral: this.goToReferral,
      onSwitchUsers: this.switchUsers,
      onGoToCallhomeUser: this.goToCallhomeUser,
      onLogout: this.logout,
    });
  };

  getRangeOptions = () => {
    const {
      locale,
      router: { location },
    } = this.props;
    return buildRangeOptions({ pathname: location.pathname, locale }) ?? [];
  };

  getMinDate = () => {
    const {
      router: { location },
      user,
      timezoneId,
    } = this.props;
    return getDateBounds({
      pathname: location.pathname,
      timezoneId,
      videoPlaybackStartsAt: user.profile.metadata.videoPlaybackStartsAt,
      videoPlaybackEndsAt: user.profile.metadata.videoPlaybackEndsAt,
    }).minDate;
  };

  getMaxDate = () => {
    const {
      router: { location },
      user,
      timezoneId,
    } = this.props;
    return getDateBounds({
      pathname: location.pathname,
      timezoneId,
      videoPlaybackStartsAt: user.profile.metadata.videoPlaybackStartsAt,
      videoPlaybackEndsAt: user.profile.metadata.videoPlaybackEndsAt,
    }).maxDate;
  };

  closeEventPlayer = () => {
    const { playingCamerasController } = this.props;
    playingCamerasController.clearPlayingCameras();
    EventActions.closeEventPlayer();
  };

  /**
   * On the sidekick page, picking a date only makes sense for the day range -
   * the 'last 7 days' range always covers the last 7 days - so switch over to
   * it for convenience. Driven by the date picker interaction rather than by a
   * `filter.date` change: playback writes the playhead time to that same date,
   * so watching it would collapse a 'last 7 days' search to a single day when
   * the user merely opens a result.
   */
  switchToDayRange = () => {
    this.props.dispatch(setRange(RANGE_DAY));
  };

  goToSettings = () => {
    navigateToSettings(this.props.router.navigate);
  };

  goToHelpCenter = () => {
    openHelpCenter();
  };

  goToReferral = () => {
    openReferral();
  };

  goToCallhomeUser = () => {
    openCallhomeUser();
  };

  exitKiosk = () => {
    window.location.href = 'https://exitkiosk';
  };

  logout = () => {
    navigateToLogout(this.props.router.navigate);
  };

  switchUsers = () => {
    const {
      router: { location, navigate },
    } = this.props;
    navigateToSwitchUsers(navigate, location);
  };

  toggleShortcutsModal = () => {
    this.props.dispatch(toggleShowKeyboardShortcuts(!this.props.showKeyboardShortcuts));
  };

  toggleLiveOn = () => this.props.handleLiveToggle(true);

  toggleLiveOff = () => this.props.handleLiveToggle(false);

  _onLocationsSelected = ({
    locationIds,
    locationTags,
  }: {
    locationIds: readonly SiteId[];
    locationTags?: readonly string[];
  }) => {
    const { locationsById, dispatch } = this.props;
    applyLocationFilter(dispatch, locationsById, locationIds, locationTags);
  };

  render() {
    const {
      isImpersonating,
      isLiveToggleOn,
      locale,
      router: { location, navigate },
      notificationCount,
      onShowAllNotifications,
      filter: { locationIds, locationTags },
      hideRelocatedControls,
      user,
      numberOfCamerasPlaying,
      query,
      locationsById,
      locations,
    } = this.props;

    const isDemoMode = FeaturesUtil.isDemoMode();
    const isKioskMode = isKiosk();
    const canPlayLiveInKiosk = FeaturesUtil.canPlayLiveInKiosk();
    const { desktopTopNavHeight } = ThemeVariables;
    const liveOnly =
      numberOfCamerasPlaying > SD_RESTRICTION_THRESHOLD || FeaturesUtil.isLiveViewOnly();
    const isTableView = location.pathname === '/clips/table';
    const cloudStoragePackageId = query.get('storagePackageId');
    const allLocationIds = locationsById ? Object.keys(locationsById) : [];

    const canAccessSpotChecks =
      FeaturesUtil.canCreateSpotChecks(user.isSolinkUser) &&
      !isKioskMode &&
      !FeaturesUtil.isLiveViewOnly();

    const isCloudStorageLocationPackageSelected = allLocationIds?.some(
      (id) => id === cloudStoragePackageId,
    );

    const liveButtonClass = classNames('live-button', {
      active: isLiveToggleOn,
      'live-only': liveOnly,
    });

    const highlightPillClass = classNames('highlight-pill', { 'highlight-live': isLiveToggleOn });

    // Conditional render
    const shouldRenderLocationSelector = !isDemoMode && !isKioskMode;
    const userMenuItems = this.getUserMenuItems();

    const tabName = getTabFromPathname(location.pathname);
    const isVideoTab = tabName === TAB_VIDEO;
    const closeEventPlayerOnDateChange = tabName === TAB_EVENTS || tabName === TAB_CLIPS;
    const onChangeRangeDate = closeEventPlayerOnDateChange ? this.closeEventPlayer : undefined;

    const canUseVideoSearch =
      VIDEO_SEARCH_ROUTES.some((route) => location.pathname.startsWith(route)) &&
      hasAccessToVideoSearch(user, locations ?? []);

    const videoPlaybackEndsAtDate = user.profile.metadata.videoPlaybackEndsAt;

    const isLocationSelectorDisabled =
      !!numberOfCamerasPlaying || isCloudStorageLocationPackageSelected;
    const aiChatEnabled = !FeaturesUtil.isLiveViewOnly() && userHasFeature(user, 'aiChatEnabled');
    const aiServiceDisplayEnabled = userHasFeature(user, 'sdmReadAccess');

    return (
      <KeyActions actions={this.shortcuts}>
        <Grid
          className="desktop-date-bar"
          gap={COLUMN_GAP}
          gridTemplateColumns="1fr auto"
          height={`${desktopTopNavHeight}px`}
        >
          <Grid className="left-wrapper" gap={COLUMN_GAP}>
            <Routes>
              {!hideRelocatedControls &&
                DATE_PICKER_ROUTES.map((route) => (
                  <Route
                    path={`${route}/*`}
                    key={route}
                    element={
                      <Flex width="auto" alignItems="center" className="date-picker-live-wrapper">
                        {route === VIDEO_URL_PREFIX && <div className={highlightPillClass}></div>}
                        <GlobalDatePicker
                          handleToggleClick={this.toggleLiveOff}
                          isOnInToggle={!isLiveToggleOn}
                          showLiveToggle={isVideoTab}
                          minDate={this.getMinDate()}
                          maxDate={this.getMaxDate()}
                          selectedLocationIds={locationIds}
                          disabled={liveOnly || isTableView}
                          onChangeRangeDate={onChangeRangeDate}
                          onDatePicked={route === SIDEKICK_URL ? this.switchToDayRange : undefined}
                          showTimeInput
                          navigateByRange={chevronsStepByRange(route)}
                        />
                        {(!isKioskMode || canPlayLiveInKiosk) &&
                          route === VIDEO_URL_PREFIX &&
                          !videoPlaybackEndsAtDate && (
                            <div
                              className={liveButtonClass}
                              data-testid={`date-picker-${liveButtonClass}`}
                              onClick={this.toggleLiveOn}
                            >
                              {locale.general.live}
                            </div>
                          )}
                      </Flex>
                    }
                  />
                ))}
            </Routes>
            <Routes>
              {!hideRelocatedControls &&
                RANGE_PICKER_ROUTES.map((route) => (
                  <Route
                    path={`${route}/*`}
                    key={route}
                    element={
                      <RangeSelector
                        defaultRange={RANGE_DAY}
                        rangeOptions={this.getRangeOptions()}
                      />
                    }
                  />
                ))}
            </Routes>
            <Routes>
              {shouldRenderLocationSelector &&
                !hideRelocatedControls &&
                LOCATION_PICKER_ROUTES.map((route) => (
                  <Route
                    path={`${route}/*`}
                    key={route}
                    element={
                      <LocationSelector
                        multiSelect
                        selectedLocationTags={locationTags}
                        selectedLocationIds={locationIds}
                        onLocationsSelected={this._onLocationsSelected}
                        context={SELECT_LOCATIONS_CONTEXT.GLOBAL_SELECTOR}
                        disabled={isLocationSelectorDisabled}
                      />
                    }
                  />
                ))}
            </Routes>
            <Routes>
              {canUseVideoSearch &&
                VIDEO_SEARCH_ROUTES.map((route) => (
                  <Route
                    path={`${route}/*`}
                    key={route}
                    element={<SmartSearch navigate={navigate} />}
                  />
                ))}
            </Routes>
          </Grid>

          <Flex
            alignItems="center"
            justifyContent="flex-end"
            gap={COLUMN_GAP}
            className="right-wrapper"
          >
            <Flex alignItems="center">
              <KeyboardShortcutsDialogGated
                isOpen={this.props.showKeyboardShortcuts}
                onClose={this.toggleShortcutsModal}
              />
            </Flex>
            {isDemoMode && <div className="demo">Demo</div>}
            <div className="desktop-date-bar-ai-tools">
              {canAccessSpotChecks ? (
                <Routes>
                  {SIDEKICK_ROUTES.map((route) => (
                    <Route path={`${route}/*`} key={route} element={<Sidekick />} />
                  ))}
                </Routes>
              ) : null}
              {aiChatEnabled ? (
                <AiChatStoreProvider>
                  <AIChat />
                </AiChatStoreProvider>
              ) : null}
            </div>
            <div className="right-side-icons-container">
              {aiServiceDisplayEnabled && (
                <SolinkButton
                  type="button"
                  variant="outline"
                  className="tw shrink-0 border-border-muted bg-background-surface text-interaction-neutral-content-default shadow-none hover:bg-background-muted hover:text-interaction-neutral-content-default dark:border-border-muted dark:bg-background-surface dark:text-interaction-neutral-content-default dark:hover:bg-background-muted dark:hover:text-interaction-neutral-content-default"
                  onClick={() => {
                    AnalyticsLogger.trackSDMEvent(SDM_EVENTS.OPEN, {
                      description: 'Accessed SDM page from dashboard',
                    });
                    window.open('/ai-service-display', '_blank', 'noopener,noreferrer');
                  }}
                >
                  <LayoutDashboard className="size-4" />
                  SDM
                  <ExternalLink className="size-4" />
                </SolinkButton>
              )}
              {!hideRelocatedControls &&
                !isKioskMode &&
                !AuthService.isGuest &&
                !canAccessAlarmStatusOnly(user) &&
                !FeaturesUtil.isLiveViewOnly() && (
                  <NotificationInbox
                    notificationCount={notificationCount}
                    onShowAll={onShowAllNotifications}
                    slim
                  />
                )}
              {!hideRelocatedControls && (
                <UserDropDownMenu
                  isImpersonating={isImpersonating}
                  menuClassName={isKioskMode ? 'kiosk-mode' : ''}
                  menuItems={userMenuItems}
                  userName={user.profile.name}
                  userEmail={user.profile.email}
                />
              )}
            </div>
            {isKioskMode && (
              <Button
                className="kiosk-close-button"
                text={locale ? locale.general.close : 'Close'}
                onClick={this.exitKiosk}
              />
            )}
          </Flex>
        </Grid>
      </KeyActions>
    );
  }
}

const ComposedDesktopHeader = (
  props: Omit<
    DesktopHeaderProps,
    | 'locale'
    | 'filter'
    | 'numberOfCamerasPlaying'
    | 'showKeyboardShortcuts'
    | 'dispatch'
    | 'router'
    | 'user'
    | 'playingCamerasController'
    | 'query'
    | 'timezoneId'
  >,
) => {
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.filter);
  const user = useUserSession();
  const playingCamerasController = usePlayingCamerasContext();
  const numberOfCamerasPlaying = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds.length,
  );
  const [query] = useQueryParams();
  const showKeyboardShortcuts = useSelector((state) => state.uiState.showKeyboardShortcuts);
  const timezoneId = useSelector((state) => state.filter.timezoneId);

  const dispatch = useDispatch();

  const { deviceIds } = useDevicesFromSites(filter.locationIds);

  // If the user has a small number of selected locations, pre-fetch their local status.
  const devicesToCheck = useMemo(() => {
    if (filter.locationIds.includes(ALL_LOCATIONS as SiteId) || filter.locationIds.length >= 5) {
      return [];
    }
    return deviceIds;
  }, [filter.locationIds, deviceIds]);

  useDevicesAreLocal(devicesToCheck);

  return (
    <DesktopHeader
      {...props}
      locale={locale}
      filter={filter}
      numberOfCamerasPlaying={numberOfCamerasPlaying}
      showKeyboardShortcuts={showKeyboardShortcuts}
      dispatch={dispatch}
      router={{ location, navigate }}
      user={user}
      playingCamerasController={playingCamerasController}
      query={query}
      timezoneId={timezoneId}
    />
  );
};

export default ComposedDesktopHeader;
